import { LocationDescriptorObject } from 'history';
import chunk from 'lodash/chunk';
import React, {
  Children,
  cloneElement,
  createRef,
  Fragment,
  MouseEvent,
  PureComponent,
  ReactElement,
  ReactNode
} from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styled, { css } from 'styled-components';
import RefHolder from '../../components/behavior/RefHolder';
import { Toggle } from '../../components/behavior/Toggle';
import { Column, Row } from '../../components/Grid';
import { Icon } from '../../components/Icon';
import {
  Navigation,
  NavigationHeader,
  NavigationLink,
  NavigationList,
  NavigationListItem,
  NavigationListItemWithTriangle,
  NavigationWrapper,
  NavigationWrapperClose
} from '../../components/Navigation';
import { Section } from '../../components/Section';
import { getSvgIconKey, SvgIcon } from '../../components/SvgIcon';
import { BookmarkApi, BookmarkContext } from '../../provider/BookmarkProvider';
import {
  withLang,
  WithLangProps
} from '../../provider/LanguageProvider/withLang';
import {
  selectLayer,
  selectMenu,
  selectSearchDetailVehicleId
} from '../../provider/Typo3Provider/selectors';
import { MenuEntry } from '../../provider/Typo3Provider/types';
import Typo3Label from '../../provider/Typo3Provider/Typo3Label';
import { getVehicleDetailSlug } from '../../provider/Typo3Provider/utils';
import {
  withTypo3,
  WithTypo3Props
} from '../../provider/Typo3Provider/withTypo3';
import DataLayerContainer from './DataLayerContainer';

interface MetaNavigationContainerProps {}

interface MetaNavigationContainerState {}

interface MenuEntryPartial {
  title: string;
  link: string;
  icon?: string;
  layout: number | string;
}

const navigationListItemIconWithLanguageCss = css`
  ${Icon} {
    width: 18px;
    height: 18px;
    margin-bottom: 0;

    svg {
      width: 18px;
      height: 18px;
      transform: rotate(0);
    }
  }
`;

const MetaIcon = styled(SvgIcon)`
  width: auto;
  height: auto;
`;

const metaIconCSS = css`
  ${MetaIcon} {
    display: inline-block;
    width: auto;
    height: auto;

    div {
      display: flex;
    }

    canvas {
      height: 18px;
      width: auto;
    }

    svg {
      display: inline-block;
      transition: fill 0.2s;
      fill: #9b9b9b;
    }
  }

  &:hover,
  ${NavigationLink}:hover {
    ${MetaIcon} svg {
      fill: #013460;
    }
  }
`;

interface MetaNavigationListItemProps {
  languages?: boolean;
}

const MetaNavigationListItem = NavigationListItem.extend`
  ${({ languages }: MetaNavigationListItemProps) =>
    languages && navigationListItemIconWithLanguageCss};

  ${metaIconCSS};
`;

const MetaNavigationListItemWithTriangle = NavigationListItemWithTriangle.extend`
  ${({ languages }: MetaNavigationListItemProps) =>
    languages && navigationListItemIconWithLanguageCss};

  ${metaIconCSS};
`;

interface MetaNavigationLinkProps {
  link: string | LocationDescriptorObject;
  locale?: string;
  flag?: string;
  languageUid?: string;
  disabled?: boolean;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  innerRef?: (ref: HTMLElement | null) => void;
}

const MetaNavigationLink = ({
  link,
  flag,
  languageUid,
  onClick,
  innerRef,
  disabled
}: MetaNavigationLinkProps) => {
  const icon = flag ? getSvgIconKey(flag) : null;
  return (
    <NavigationLink exact to={link} onClick={onClick} innerRef={innerRef} disabled={disabled}>
      <SvgIcon icon={icon || 'de'} />
      <span>
        <Typo3Label label={`lang.${languageUid || '-1'}`} />
      </span>
    </NavigationLink>
  );
};

const entriesPerColumn = 5;

interface ColumnGroupProps {
  children: ReactNode;
  GroupContainer?: ReactElement<any>;
  groupSize: number;
}

const ColumnGroup = (props: ColumnGroupProps) => {
  const children = Children.toArray(props.children);
  const GroupContainer: ReactElement<any> = props.GroupContainer || (
    <Fragment />
  );
  const groups = chunk(children, props.groupSize);
  const large = Math.ceil(12 / groups.length);
  return (
    <Fragment>
      {groups.map((group, index) => (
        <Column key={index} large={large}>
          {cloneElement(GroupContainer, {}, group)}
        </Column>
      ))}
    </Fragment>
  );
};

class MetaNavigationContainer extends PureComponent<
  MetaNavigationContainerProps &
    WithTypo3Props &
    WithLangProps &
    RouteComponentProps,
  MetaNavigationContainerState
> {
  public containerRef = createRef<HTMLElement>();

  public getWatchListEntry() {
    const [watchListEntry = null] = selectMenu(this.props.typo3, 'watchlist');
    return watchListEntry;
  }

  public getLayer() {
    return selectLayer(this.props.typo3);
  }

  public getLanguages() {
    return selectMenu(this.props.typo3, 'languages');
  }

  public getMeta() {
    return selectMenu(this.props.typo3, 'meta');
  }

  public sortLanguages(languages: MenuEntry[]) {
    const {
      typo3: { translateTypo3 },
      lang: { languageCode, countryCode }
    } = this.props;
    const locale = `${languageCode.toUpperCase()}-${countryCode.toLowerCase()}`;
    const pairs = languages.map(
      (entry): [string, MenuEntry] => [
        translateTypo3(`lang.${entry.languageUid || '-1'}`),
        entry
      ]
    );
    pairs.sort(([s0], [s1]) => s0.localeCompare(s1, locale));
    return pairs.map(([_, entry]) => entry);
  }

  public renderBookmarksEntry = ({ bookmarks }: BookmarkApi) => {
    const watchListEntry = this.getWatchListEntry();
    const { typo3 } = this.props;
    if (watchListEntry) {
      return this.renderMenuEntry(
        {
          title: `${typo3.translateTypo3('metaNav.bookmark')} (${bookmarks.length})`,
          link: watchListEntry.link,
          icon: 'favourits',
          layout: ''
        },
        20
      );
    }
    // TODO: this should never happen, watchlist-MenuEntry should always be available in Typo3Context
    return null;
  };

  public getRefCenter = (ref: HTMLElement | null) => {
    const { current: containerRef } = this.containerRef;
    if (ref && containerRef) {
      const { right, width } = ref.getBoundingClientRect();
      const { right: containerRight } = containerRef.getBoundingClientRect();
      return Math.round(-right + containerRight + width / 2 - 20);
    }
  };

  public renderMenuEntry = (menuEntry: MenuEntryPartial, index: number) => {
    const layer = this.getLayer();
    const { location } = this.props;
    const entryIcon = getSvgIconKey(menuEntry.icon);
    // if layout === '11' it is the privacy-entry, so we need to display the layer-content
    // additionally provided by Typo3
    const hasLayer = menuEntry.layout === 11 && !!layer;
    if (layer && hasLayer) {
      return (
        <Toggle
          key={`${location.pathname}:${index}`}
          preventDefault
          render={({ active, onClick, setInactive }) => (
            <RefHolder
              render={({ ref, setRef }) => (
                <MetaNavigationListItemWithTriangle>
                  <NavigationLink
                    to={menuEntry.link}
                    onClick={onClick}
                    innerRef={setRef}
                  >
                    {entryIcon && <MetaIcon icon={entryIcon} />}
                    <span>{menuEntry.title}</span>
                  </NavigationLink>
                  {active && (
                    <NavigationWrapper
                      showTriangle
                      triangleRight={ref ? this.getRefCenter(ref) : undefined}
                    >
                      <NavigationWrapperClose onClick={setInactive}>
                        <SvgIcon icon="close" />
                      </NavigationWrapperClose>
                      <DataLayerContainer layer={layer} />
                    </NavigationWrapper>
                  )}
                </MetaNavigationListItemWithTriangle>
              )}
            />
          )}
        />
      );
    }
    return (
      <MetaNavigationListItem key={index}>
        <NavigationLink to={menuEntry.link}>
          {entryIcon && <MetaIcon icon={entryIcon} />}
          <span>{menuEntry.title}</span>
        </NavigationLink>
      </MetaNavigationListItem>
    );
  };

  public renderLanguagesEntry = () => {
    const {
      typo3: { languageUid, pathContent }
    } = this.props;

    const vehicleId = selectSearchDetailVehicleId(this.props.typo3);
    const vehicleSlug = vehicleId ? getVehicleDetailSlug(this.props.typo3, vehicleId) : undefined;

    const currentFlag = pathContent?.meta.flag ? pathContent.meta.flag : 'de';

    const languages = this.sortLanguages(this.getLanguages());
    const languagesRow = <Navigation type='language'>
      <Row noGap>
        <ColumnGroup
          GroupContainer={<NavigationList />}
          groupSize={entriesPerColumn}
        >
          {languages.map((languageEntry, index) => (
            <MetaNavigationListItem languages key={index}>
              <MetaNavigationLink
                link={`${languageEntry.link}${vehicleSlug || ''}`}
                disabled={!languageEntry.available}
                languageUid={languageEntry.languageUid}
                flag={languageEntry.flag}
              />
            </MetaNavigationListItem>
          ))}
        </ColumnGroup>
      </Row>
    </Navigation>;

    return (
      <Toggle
        preventDefault
        render={({ active, onClick, setInactive }) => (
          <RefHolder
            render={({ ref, setRef }) => (
              <MetaNavigationListItemWithTriangle languages>
                <MetaNavigationLink
                  link="#"
                  languageUid={languageUid.toString(10)}
                  flag={currentFlag}
                  onClick={onClick}
                  innerRef={setRef}
                />
                {active && (
                  <NavigationWrapper
                    width={800}
                    showTriangle
                    triangleRight={ref ? this.getRefCenter(ref) : undefined}
                  >
                    <NavigationWrapperClose onClick={setInactive}>
                      <SvgIcon icon="close" />
                    </NavigationWrapperClose>
                    <Row noGap>
                      <Column>
                        <NavigationHeader>
                          <Typo3Label label="meta.language.header">
                            Sprachenauswahl
                          </Typo3Label>
                        </NavigationHeader>
                      </Column>
                    </Row>
                    {languagesRow}
                  </NavigationWrapper>
                )}
              </MetaNavigationListItemWithTriangle>
            )}
          />
        )}
      />
    );
  };

  public render() {
    const meta = this.getMeta();
    const languages = this.getLanguages();

    return (
      <Navigation type='meta'>
        <Row noGap>
          <Column>
            <Section>
              <NavigationList inlined innerRef={this.containerRef}>
                <BookmarkContext.Consumer>
                  {this.renderBookmarksEntry}
                </BookmarkContext.Consumer>
                {meta.map(this.renderMenuEntry)}
                {languages.length > 0 && this.renderLanguagesEntry()}
              </NavigationList>
            </Section>
          </Column>
        </Row>
      </Navigation>
    );
  }
}

export default withRouter(withTypo3(withLang(MetaNavigationContainer)));
